/**
 * Chrome shared by both halves of the Meta page.
 *
 * PvP and Raids are different questions over different data from different
 * sources, but they are the same PAGE, and a trainer should not have to learn
 * two sets of controls to move between them. The filter chips, the type
 * labelling and the sprite row live here so the two panels cannot drift into
 * looking like separate products.
 */
import type { ReactNode } from "react";
import { FilterChip } from "@/components/DexFilterMenu";
import { ALL_TYPES, REGIONS } from "@/lib/types";

export const typeLabel = (key: string) =>
  ALL_TYPES.find((t) => t.key === key)?.label ?? key;

export const typeColor = (key: string) =>
  ALL_TYPES.find((t) => t.key === key)?.color ?? "var(--text-tertiary)";

export const regionLabel = (key: string) =>
  REGIONS.find((r) => r.key === key)?.label ?? key;

/**
 * A filter row, built from the Dex page's own FilterChip.
 *
 * Deliberately the SAME component, not a copy that looks like it: the type
 * chips here carry the same emoji and the same colour they do on the Pokédex
 * filter and the species page, so a trainer who has learned what a green leaf
 * chip means has learned it once. It is also why these cannot drift.
 */
export function ChipRow({
  testId,
  options,
  value,
  onPick,
  allLabel = "All",
}: {
  testId: string;
  options: { key: string; label: string; color?: string; icon?: ReactNode }[];
  value: string;
  onPick: (key: string) => void;
  allLabel?: string;
}) {
  return (
    // Scrolls sideways rather than wrapping to four rows: eighteen types would
    // otherwise push the ranking itself off the screen, which is the page.
    <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5" data-testid={`filter-${testId}`}>
      <FilterChip
        label={allLabel}
        active={value === "all"}
        onClick={() => onPick("all")}
        testId={`filter-${testId}-all`}
      />
      {options.map((o) => (
        <FilterChip
          key={o.key}
          label={o.label}
          active={value === o.key}
          onClick={() => onPick(o.key)}
          testId={`filter-${testId}-${o.key}`}
          accent={o.color}
          icon={o.icon ? <span aria-hidden>{o.icon}</span> : undefined}
        />
      ))}
    </div>
  );
}

/**
 * The type badges a row carries.
 *
 * `lit` is the type currently being filtered on: it fills, the others stay
 * quiet but visible, because "it is also your best Rock" is the argument for
 * building something once instead of twice.
 */
export function TypeBadge({
  type,
  lit,
  children,
  title,
  testId,
}: {
  type: string;
  lit: boolean;
  children: React.ReactNode;
  title?: string;
  testId?: string;
}) {
  return (
    <span
      className="text-[8px] font-bold uppercase tracking-wide px-1.5 py-px rounded-full"
      style={{
        background: lit ? typeColor(type) : `${typeColor(type)}26`,
        color: lit ? "#0B0E1A" : typeColor(type),
        border: `1px solid ${typeColor(type)}${lit ? "" : "40"}`,
      }}
      title={title}
      data-testid={testId}
    >
      {children}
    </span>
  );
}


/**
 * A move, in its own type's colour.
 *
 * Replaces a sentence naming the moves. On a card that already shows the
 * Pokemon, the moveset is the actionable half, and two coloured pills read at
 * a glance where "make sure your strongest Zarude knows Vine Whip and Power
 * Whip" had to be read as prose. The colour is the move's TYPE, which is also
 * the thing a trainer is checking for: a grass move on a grass attacker.
 */
export function MoveChip({ name, type }: { name: string; type: string }) {
  const color = typeColor(type);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
      style={{
        background: `color-mix(in srgb, ${color} 18%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 55%, transparent)`,
        color: "var(--text-primary)",
      }}
      title={`${name} (${typeLabel(type)})`}
      data-testid={`move-chip-${type}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ background: color }}
        aria-hidden
      />
      {name}
    </span>
  );
}
